import { State, Action, StateContext, Selector } from '@ngxs/store';
import { SnackbarConfig, SnackbarType } from '@core/models';
import {
  EnableMobileModeAction,
  DisableMobileModeAction,
  OpenSnackbarAction,
  CloseSnackbarAction,
  ShowSpinnerAction,
  HideSpinnerAction,
  OpenMenuAction,
  CloseMenuAction,
  SetThemeAction,
  ToggleIsInferiorBrowserAction,
  UpdateSignalRConnectionStatusAction,
  RegisterSignalRHandlersForUiAction,
  CloseToastrAction,
  OpenToastrAction,
} from './ui.actions';
import { Injectable } from '@angular/core';
import { ToastrConfig } from '@core/models/ui/toastr-config';

export class UiStateModel {
  public showMenu: boolean;
  public theme: string;
  public isMobileMode: boolean;
  public showSpinner: boolean;
  public signalRConnected: boolean;
  public signalRHandlersRegistered: boolean;
  public isInferiorBrowser: boolean;
  public snackbarConfig: SnackbarConfig;
  public toastrConfig: ToastrConfig;
}

@State<UiStateModel>({
  name: 'ui',
  defaults: {
    showMenu: true,
    theme: 'light-theme',
    isMobileMode: false,
    showSpinner: false,
    signalRConnected: false,
    signalRHandlersRegistered: false,
    isInferiorBrowser: false,
    snackbarConfig: { show: false },
    toastrConfig: { show: false }
  }
})
@Injectable()
export class UiState {
  constructor(
  ) { }

  @Selector()
  static getShowMenu(state: UiStateModel) {
    return state.showMenu;
  }

  @Selector()
  static getIsInferiorBrowser(state: UiStateModel) {
    return state.isInferiorBrowser;
  }

  @Selector()
  static getTheme(state: UiStateModel) {
    return state.theme;
  }

  @Selector()
  static getIsMobileMode(state: UiStateModel) {
    return state.isMobileMode;
  }

  @Selector()
  static getSignalRConnectionState(state: UiStateModel) {
    return state.signalRConnected;
  }

  @Selector()
  static getShowSpinner(state: UiStateModel) {
    return state.showSpinner;
  }

  @Selector()
  static getSnackbarConfig(state: UiStateModel) {
    return state.snackbarConfig;
  }

  @Selector()
  static getToastrConfig(state: UiStateModel) {
    return state.toastrConfig;
  }  

  @Action(EnableMobileModeAction)
  enableMobileModeAction(ctx: StateContext<UiStateModel>, action: EnableMobileModeAction) {
    ctx.patchState({
      isMobileMode: true
    });
  }

  @Action(OpenMenuAction)
  openMenuAction(ctx: StateContext<UiStateModel>, action: OpenMenuAction) {
    ctx.patchState({
      showMenu: true
    });
  }

  @Action(CloseMenuAction)
  closeMenuAction(ctx: StateContext<UiStateModel>, action: CloseMenuAction) {
    ctx.patchState({
      showMenu: false
    });
  }

  @Action(DisableMobileModeAction)
  disableMobileModeAction(ctx: StateContext<UiStateModel>, action: DisableMobileModeAction) {
    ctx.patchState({
      isMobileMode: false
    });
  }

  @Action(ToggleIsInferiorBrowserAction)
  toggleIsInferiorBrowserAction(ctx: StateContext<UiStateModel>, action: ToggleIsInferiorBrowserAction) {

    ctx.patchState({
      isInferiorBrowser: action.payload.isInferiorBrowser
    });
  }

  @Action(SetThemeAction)
  enableDarkThemeAction(ctx: StateContext<UiStateModel>, action: SetThemeAction) {
    ctx.patchState({
      theme: action.payload.theme
    });
  }

  @Action(OpenSnackbarAction)
  openSnackbarAction(ctx: StateContext<UiStateModel>, action: OpenSnackbarAction) {
    ctx.patchState({
      snackbarConfig: { show: true, message: action.payload.message, type: action.payload.type, problemDetail: action.payload.problemDetail }
    });
  }

  @Action(OpenToastrAction)
  openToasterAction(ctx: StateContext<UiStateModel>, action: OpenToastrAction) {
    ctx.patchState({
      toastrConfig: { show: true, message: action.payload.message, type: action.payload.type, problemDetail: action.payload.problemDetail }
    });
  } 

  @Action(CloseToastrAction)
  closeToasterAction(ctx: StateContext<UiStateModel>, action: CloseToastrAction) {
    ctx.patchState({
      toastrConfig: { show: false }
    });
  }

  @Action(CloseSnackbarAction)
  closeSnackbarAction(ctx: StateContext<UiStateModel>, action: CloseSnackbarAction) {
    ctx.patchState({
      snackbarConfig: { show: false }
    });
  }

  @Action(ShowSpinnerAction)
  showSpinnerAction(ctx: StateContext<UiStateModel>, action: ShowSpinnerAction) {
    ctx.patchState({
      showSpinner: true
    });
  }

  @Action(HideSpinnerAction)
  hideSpinnerAction(ctx: StateContext<UiStateModel>, action: HideSpinnerAction) {
    ctx.patchState({
      showSpinner: false
    });
  }

  @Action(UpdateSignalRConnectionStatusAction)
  updateSignalRConnectionStatusAction(ctx: StateContext<UiStateModel>, action: UpdateSignalRConnectionStatusAction) {
    ctx.patchState({
      signalRConnected: action.payload.connected
    });
  }

  @Action(RegisterSignalRHandlersForUiAction)
  registerSignalRHandlersForUiAction({ getState, dispatch, patchState }: StateContext<UiStateModel>, action: RegisterSignalRHandlersForUiAction) {
    const state = getState();

    if (!state.signalRHandlersRegistered) {

      patchState({
        signalRHandlersRegistered: true
      });
    }
  }
}
